import {
  BooleanValidation,
  ConfigOptionValidation,
  StringValidation,
} from '../../../utils/component-validations';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Container from './container.gjs';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './button.module.scss';

const ELEMENT_TAG_DEFAULT = 'button';

/**
 * Button component for SPDS
 *
 * Arguments
 * @argument {boolean} absolute position the button absolutely
 * @argument {boolean} autofocus automatically focus the button on render
 * @argument {boolean} column arrange content in a column
 * @argument {boolean} disabled disable the button
 * @argument {boolean} error apply error styling
 * @argument {boolean} fitContent size to fit content
 * @argument {boolean} fitContentHeight height sized to fit content
 * @argument {boolean} fitContentWidth width sized to fit content
 * @argument {boolean} fixed apply fixed positioning
 * @argument {boolean} iconOnly button only contains an icon
 * @argument {boolean} inline display button inline
 * @argument {string} size button size, options: 'normal', 'small'
 * @argument {string} tag HTML tag to use for rendering, defaults to 'button'
 * @argument {string} text text content for the button
 * @argument {string} type button type attribute, defaults to 'button'
 */
export default class SpdsCoreButton extends Super {
  arguments = [
    new BooleanValidation({
      optional: [
        'absolute',
        'autofocus',
        'column',
        'disabled',
        'error',
        'fitContent',
        'fitContentHeight',
        'fitContentWidth',
        'fixed',
        'iconOnly',
        'inline',
      ],
    }),
    new ConfigOptionValidation({ optional: [{ name: 'size', options: ['normal', 'small'] }] }),
    new StringValidation({ optional: ['tag', 'text', 'type'] }),
  ];
  baseClass = 'spds-button';
  classesToMapToArgs = ['iconOnly', 'size'];
  classesToMapToAttrs = ['hasBorder'];
  componentName = 'Spds::Core::Button';
  styles = styles;

  @tracked baseElement;

  @reads('args.tag', ELEMENT_TAG_DEFAULT) tag;
  @reads('args.type', ELEMENT_TAG_DEFAULT) type;

  /**
   * determines if role attribute should be set
   * @return {string|undefined} button role attribute value when not a button element
   */
  get role() {
    return this.tag === ELEMENT_TAG_DEFAULT ? undefined : ELEMENT_TAG_DEFAULT;
  }

  /**
   * determines the variant style of the button for data-test attributes
   * @return {string} button variant value
   */
  get variant() {
    if (this.link) return 'link';
    if (this.variantClass) return this.variantClass.split('-').at(-1);

    return this.tag;
  }

  <template>
    <Container
      @absolute={{@absolute}}
      @column={{@column}}
      @content={{@text}}
      @error={{@error}}
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @fixed={{@fixed}}
      @inline={{@inline}}
      @tag={{this.tag}}
      class={{this.classes}}
      data-test-spds-button={{this.variant}}
      disabled={{@disabled}}
      role={{this.role}}
      type={{this.type}}
      ...attributes
      {{SpdsAutofocus @autofocus}}
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      {{yield}}
    </Container>
  </template>
}
